import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

// Performance level calculations
export const getPerformanceLevel = (score) => {
  if (score >= 90) return 'Expert';
  if (score >= 80) return 'Advanced';
  if (score >= 70) return 'Intermediate';
  if (score >= 60) return 'Beginner';
  return 'Novice';
};

// Performance color based on score
export const getPerformanceColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
};

// Performance stars rating
export const getPerformanceStars = (score) => {
  if (score >= 90) return 5;
  if (score >= 80) return 4;
  if (score >= 70) return 3;
  if (score >= 60) return 2;
  return 1;
};

// Trend icon based on current vs previous values
export const getTrendIcon = (current, previous) => {
  if (!previous) return <TrendingFlat />;
  if (current > previous) return <TrendingUp color="success" />;
  if (current < previous) return <TrendingDown color="error" />;
  return <TrendingFlat />;
};

// Percentile calculation
export const getPercentile = (rank, total) => {
  if (!rank || !total) return 0;
  return Math.round(((total - rank) / total) * 100);
};

// Performance insight based on score
export const getPerformanceInsight = (score) => {
  if (score >= 90) return { level: 'Excellent', color: 'success', icon: 'star' };
  if (score >= 80) return { level: 'Very Good', color: 'success', icon: 'check' };
  if (score >= 70) return { level: 'Good', color: 'warning', icon: 'trending_up' };
  if (score >= 60) return { level: 'Average', color: 'warning', icon: 'trending_flat' };
  return { level: 'Needs Improvement', color: 'error', icon: 'trending_down' };
};

// Time insight based on time spent and difficulty
export const getTimeInsight = (timeSpent, difficulty) => {
  const avgTime = difficulty === 'easy' ? 30 : difficulty === 'medium' ? 60 : 90;
  if (timeSpent < avgTime * 0.7) return { insight: 'Very Fast', color: 'success' };
  if (timeSpent < avgTime) return { insight: 'Good Pace', color: 'info' };
  if (timeSpent < avgTime * 1.3) return { insight: 'Slightly Slow', color: 'warning' };
  return { insight: 'Too Slow', color: 'error' };
};

// Difficulty color mapping
export const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'easy': return 'success';
    case 'medium': return 'warning';
    case 'hard': return 'error';
    default: return 'default';
  }
};

// Time formatting utilities
export const formatTime = (seconds) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

export const formatTimeFromString = (timeString) => {
  if (!timeString) return 'N/A';
  const parts = timeString.split(':');
  if (parts.length === 3) {
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const seconds = parseInt(parts[2]);
    const totalMinutes = hours * 60 + minutes;
    return `${totalMinutes}m ${seconds}s`;
  }
  return timeString;
};

// Accuracy calculation
export const calculateAccuracy = (correct, attempted) => {
  if (!attempted || attempted === 0) return 0;
  return (correct / attempted) * 100;
};

// Total question time calculation
export const calculateTotalQuestionTime = (perQuestionTime) => {
  if (!perQuestionTime || perQuestionTime.length === 0) {
    return 0;
  }
  return perQuestionTime.reduce((total, timing) => total + (timing.timeSpent || 0), 0);
};

// Speed calculation (questions per minute)
export const calculateSpeed = (totalQuestions, totalTimeMinutes) => {
  if (!totalTimeMinutes || totalTimeMinutes === 0) return 0;
  return totalQuestions / totalTimeMinutes;
};

// Time efficiency calculation
export const calculateTimeEfficiency = (actualTime, expectedTime) => {
  if (!expectedTime || expectedTime === 0) return 0;
  return Math.min((expectedTime / actualTime) * 100, 100);
};

// Progress calculation
export const calculateProgress = (current, target) => {
  if (!target || target === 0) return 0;
  return Math.min((current / target) * 100, 100);
};

// Study streak calculation
export const calculateStudyStreak = (testDates) => {
  if (!testDates || testDates.length === 0) return 0;
  
  const sortedDates = testDates
    .map(date => new Date(date))
    .sort((a, b) => b - a);
  
  let streak = 1;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = 0; i < sortedDates.length - 1; i++) {
    const currentDate = new Date(sortedDates[i]);
    const nextDate = new Date(sortedDates[i + 1]);
    
    const diffTime = currentDate - nextDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
};

// Performance trend analysis
export const analyzePerformanceTrend = (scores) => {
  if (!scores || scores.length < 2) return { trend: 'stable', change: 0 };
  
  const recentScores = scores.slice(-5); // Last 5 scores
  const olderScores = scores.slice(-10, -5); // Previous 5 scores
  
  const recentAvg = recentScores.reduce((sum, score) => sum + score, 0) / recentScores.length;
  const olderAvg = olderScores.reduce((sum, score) => sum + score, 0) / olderScores.length;
  
  const change = recentAvg - olderAvg;
  
  if (change > 5) return { trend: 'improving', change: change.toFixed(1) };
  if (change < -5) return { trend: 'declining', change: change.toFixed(1) };
  return { trend: 'stable', change: change.toFixed(1) };
};

// Subject strength/weakness analysis
export const analyzeSubjectPerformance = (subjectPerformance) => {
  if (!subjectPerformance || subjectPerformance.length === 0) {
    return { strengths: [], weaknesses: [], recommendations: [] };
  }
  
  const strengths = subjectPerformance
    .filter(subject => subject.accuracy >= 80)
    .sort((a, b) => b.accuracy - a.accuracy);
  
  const weaknesses = subjectPerformance
    .filter(subject => subject.accuracy < 70)
    .sort((a, b) => a.accuracy - b.accuracy);
  
  const recommendations = weaknesses.map(subject => ({
    subject: subject.name,
    currentAccuracy: subject.accuracy,
    targetAccuracy: 80,
    improvementNeeded: 80 - subject.accuracy,
    priority: subject.accuracy < 50 ? 'high' : 'medium'
  }));
  
  return { strengths, weaknesses, recommendations };
}; 